import React, {FC, useMemo} from 'react';

import {useInitialEffect} from '../../../libs/shared/helpers/src/react/hooks/useEffectHelpers';
import {ErrorWithData} from '../../../libs/shared/helpers/src/types/customErrors';
import {useInteractionContext} from '../../../libs/shared/ui/src/interactionTracking/interactionContext';
import {build12Or24hOptionsForFormat, TimeFormatsEnum} from '../../../src/helpers/dateTimeHelpers';
import {reportToBugsnag} from '../../services/bugsnag/bugsnag';
import {DateTimeFormatterBase} from './dateTimeFormatterBase';

/*
 * Constants.
 */

const commonFormat: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
};

const h12Format: Intl.DateTimeFormatOptions = {
  ...commonFormat,
  ...build12Or24hOptionsForFormat(TimeFormatsEnum.H12),
};

const h24Format: Intl.DateTimeFormatOptions = {
  ...commonFormat,
  ...build12Or24hOptionsForFormat(TimeFormatsEnum.H24),
};

/*
 * Props.
 */

interface AbsoluteDateTimeFormatterProps {
  /** The date to format. */
  value: number;
  /** Wether we should display 12h or 24h time. */
  timeFormat?: TimeFormatsEnum;
}

/*
 * Component.
 */

export const AbsoluteDateTimeFormatter: FC<AbsoluteDateTimeFormatterProps> = ({value, timeFormat}) => {
  const date = useMemo(() => new Date(value), [value]);
  const context = useInteractionContext();
  const hasValidDate = useMemo(() => !isNaN(date.valueOf()), [date]);

  useInitialEffect(() => {
    if (!hasValidDate) {
      reportToBugsnag(
        new ErrorWithData('Trying to format an invalid date', {
          date,
          value,
          interactionContext: context,
        }),
      );
    }
  });

  // Return nothing in case of an invalid date to prevent the app from crashing.
  if (!hasValidDate) {
    return null;
  }

  const format = getAbsoluteDateTimeFormat(timeFormat, date);
  return <DateTimeFormatterBase value={date} format={format} />;
};

/*
 * Helpers.
 */

function getAbsoluteDateTimeFormat(
  timeFormat: TimeFormatsEnum | undefined,
  date: Date,
): Intl.DateTimeFormatOptions {
  const format = timeFormat === TimeFormatsEnum.H24 ? h24Format : h12Format;
  const shouldShowYear = new Date().getFullYear() !== date.getFullYear();
  return shouldShowYear ? {...format, year: 'numeric'} : format;
}
